import React from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cn } from '@thingslog/ui-components';
import { CHART_DATA_POINT_COLOR_KEY } from './chartSeries';

export interface ChartTooltipPayload {
  color?: string;
  dataKey?: string | number;
  fill?: string;
  name?: string;
  payload?: { [key: string]: string | number };
  value?: number | string;
}

export interface ChartTooltipContentProps {
  active?: boolean;
  markerClassName?: string;
  label?: string | number;
  payload?: ChartTooltipPayload[];
  fallbackColors: string[];
  labelFormatter?: (
    value: string | number,
    dataPoint?: { [key: string]: string | number }
  ) => string;
  payloadFilter?: (payload: ChartTooltipPayload[]) => ChartTooltipPayload[];
  valueFormatter?: (value: number | string, name: string) => [string, string];
}

export interface ChartLegendPayload {
  color?: string;
  id?: string;
  inactive?: boolean;
  value?: string;
}

export interface ChartLegendContentProps {
  aside?: boolean;
  markerClassName?: string;
  onToggleSeries?: (dataKey: string) => void;
  payload?: ChartLegendPayload[];
}

interface ChartWidgetFrameProps {
  children: React.ReactNode;
  /** Merged over the frame's own classes, so `p-0` drops the default padding. */
  className?: string;
}

interface ChartEmptyStateProps {
  description: string;
  descriptionKey?: string;
}

interface ChartOverflowStateProps {
  onRender: () => void;
  sliceCount: number;
}

interface ChartDataDensityIndicatorProps {
  renderedPoints: number;
  totalPoints: number;
}

interface ChartPlotAreaOffset {
  height: number;
  left: number;
  top: number;
  width: number;
}

interface ChartDataDensityOverlayProps extends ChartDataDensityIndicatorProps {
  // Injected by recharts' <Customized> when it clones the element with the
  // chart's internal state; never passed at the call site.
  offset?: ChartPlotAreaOffset;
}

export const CHART_LEGEND_MAX_HEIGHT = 72;

// Hard pixel cap for the side ("aside") legend. The legend never grows wider
// than this; entries longer than the available width wrap onto multiple lines
// instead of stretching the sidebar (and are never truncated).
export const CHART_LEGEND_ASIDE_MAX_WIDTH = 160;

export const chartTooltipWrapperStyle: React.CSSProperties = {
  border: 'none',
  outline: 'none',
};

const chartLegendBottomWrapperStyle: React.CSSProperties = {
  color: '#475569',
  fontSize: 12,
  maxHeight: CHART_LEGEND_MAX_HEIGHT,
  overflowX: 'hidden',
  overflowY: 'auto',
};

// Bottom clearance kept free of legend content so the scrollable side legend
// never extends down into the x-axis label band on its left (otherwise a
// scrolled marker dot lands at the same height as the last tick label and
// overlaps it). Paired with `verticalAlign: 'top'` so the reserved space sits
// at the bottom, over the axis, rather than being split top/bottom.
const CHART_LEGEND_ASIDE_BOTTOM_CLEARANCE = '3rem';

const chartLegendAsideWrapperStyle: React.CSSProperties = {
  color: '#475569',
  fontSize: 12,
  maxWidth: CHART_LEGEND_ASIDE_MAX_WIDTH,
  maxHeight: `calc(100% - ${CHART_LEGEND_ASIDE_BOTTOM_CLEARANCE})`,
  overflowX: 'hidden',
  overflowY: 'auto',
};

export interface ChartLegendRenderProps {
  layout: 'horizontal' | 'vertical';
  align: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
  wrapperStyle: React.CSSProperties;
  content: JSX.Element;
}

/**
 * Builds the Recharts <Legend> layout props for either the default bottom
 * placement or the "aside" placement on the right side of the widget. In both
 * cases the legend never truncates entry names and scrolls when the entries
 * overflow the available space.
 */
export const getChartLegendProps = (options: {
  aside?: boolean;
  markerClassName?: string;
  onToggleSeries?: (dataKey: string) => void;
}): ChartLegendRenderProps => {
  const { aside = false, markerClassName, onToggleSeries } = options;

  if (aside) {
    return {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'top',
      wrapperStyle: chartLegendAsideWrapperStyle,
      content: (
        <ChartLegendContent
          aside
          markerClassName={markerClassName}
          onToggleSeries={onToggleSeries}
        />
      ),
    };
  }

  return {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom',
    wrapperStyle: chartLegendBottomWrapperStyle,
    content: (
      <ChartLegendContent markerClassName={markerClassName} onToggleSeries={onToggleSeries} />
    ),
  };
};

export const chartMargin = {
  top: 12,
  right: 18,
  left: 0,
  bottom: 8,
};

/**
 * Debounce (ms) for Recharts `ResponsiveContainer` resize handling.
 *
 * With the default (`debounce={0}`) the ResizeObserver callback runs
 * synchronously; measuring and re-rendering the chart can change the observed
 * element's size again within the same frame, which the browser reports as the
 * benign-but-noisy "ResizeObserver loop completed with undelivered
 * notifications" runtime error (seen e.g. when switching a widget's chart
 * type). Debouncing moves the size update onto a later task, breaking the
 * synchronous loop. Initial sizing is unaffected — `ResponsiveContainer`
 * measures via a mount effect, not through the debounced resize handler — so
 * there is no first-paint flash.
 */
export const CHART_RESIZE_DEBOUNCE_MS = 200;

export const chartGridProps = {
  stroke: '#e5e7eb',
  strokeDasharray: '4 8',
  vertical: false,
};

export const chartAxisTick = {
  fill: '#64748b',
  fontSize: 11,
};

export const chartYAxisWidth = 42;

export const ChartWidgetFrame = ({ children, className }: ChartWidgetFrameProps): JSX.Element => {
  return (
    <div
      className={cn(
        'flex h-full w-full flex-col bg-gradient-to-br from-white via-white to-slate-50 p-3',
        className
      )}
    >
      {children}
    </div>
  );
};

export const ChartEmptyState = ({
  description,
  descriptionKey,
}: ChartEmptyStateProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <ChartWidgetFrame>
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="rounded-md border border-dashed border-slate-300 bg-white/70 px-4 py-3 text-center shadow-sm">
          <p className="text-sm font-medium text-slate-700">
            {t('dashboard_no_data_available', 'No data available')}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {descriptionKey ? t(descriptionKey, description) : description}
          </p>
        </div>
      </div>
    </ChartWidgetFrame>
  );
};

/**
 * Shown instead of a chart that would render too many marks to be readable.
 * Nothing is drawn until the user explicitly asks for it, because the render
 * itself is what makes the widget slow.
 */
export const ChartOverflowState = ({
  onRender,
  sliceCount,
}: ChartOverflowStateProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <ChartWidgetFrame>
      <Box className="flex min-h-0 flex-1 items-center justify-center p-2">
        <Alert severity="warning" className="max-w-md text-left">
          <AlertTitle>
            {t('dashboard_widget_too_many_slices', 'Too many slices to display')}
          </AlertTitle>
          {t(
            'dashboard_widget_too_many_slices_description',
            'This widget has {{slices}} slices. Drawing them all will make the dashboard slow to load and interact with, and the chart will be hard to read.',
            { slices: sliceCount }
          )}
          <Box className="mt-2">
            <Button
              color="warning"
              variant="outlined"
              size="small"
              className="whitespace-nowrap"
              onClick={onRender}
            >
              {t('dashboard_widget_render_anyway', 'Display anyway')}
            </Button>
          </Box>
        </Alert>
      </Box>
    </ChartWidgetFrame>
  );
};

export const ChartDataDensityIndicator = ({
  renderedPoints,
  totalPoints,
}: ChartDataDensityIndicatorProps): JSX.Element => {
  const { t } = useTranslation();
  const isSampled = renderedPoints < totalPoints;
  const label = isSampled ? `${renderedPoints} / ${totalPoints}` : `${totalPoints}`;

  return (
    <span
      className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase leading-4 shadow-sm ${
        isSampled
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      }`}
      title={
        isSampled
          ? t(
              'dashboard_chart_sampled_points_tooltip',
              'Showing a sampled set of real data points. Zoom in to reveal omitted points.'
            )
          : t('dashboard_chart_all_points_tooltip', 'Showing every data point in the current view.')
      }
    >
      {label}
    </span>
  );
};

const DENSITY_OVERLAY_WIDTH = 160;
const DENSITY_OVERLAY_HEIGHT = 28;
const DENSITY_OVERLAY_MARGIN = 8;

/**
 * Plot-area-anchored wrapper for `ChartDataDensityIndicator`. Rendered inside
 * the chart via recharts' `<Customized component={...} />`, which injects the
 * plot area `offset` — so the badge sits at the top-right of the plotted data
 * and never covers elements laid out around it (e.g. the aside legend).
 */
export const ChartDataDensityOverlay = ({
  offset,
  renderedPoints,
  totalPoints,
}: ChartDataDensityOverlayProps): JSX.Element | null => {
  if (!offset) {
    return null;
  }

  return (
    <foreignObject
      x={offset.left + offset.width - DENSITY_OVERLAY_WIDTH - DENSITY_OVERLAY_MARGIN}
      y={offset.top + DENSITY_OVERLAY_MARGIN}
      width={DENSITY_OVERLAY_WIDTH}
      height={DENSITY_OVERLAY_HEIGHT}
      className="pointer-events-none overflow-visible"
    >
      <div className="flex justify-end">
        <ChartDataDensityIndicator renderedPoints={renderedPoints} totalPoints={totalPoints} />
      </div>
    </foreignObject>
  );
};

export const ChartTooltipContent = ({
  active,
  label,
  payload,
  fallbackColors,
  labelFormatter,
  markerClassName = 'rounded-full',
  payloadFilter,
  valueFormatter,
}: ChartTooltipContentProps): JSX.Element | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const visiblePayload = payloadFilter ? payloadFilter(payload) : payload;

  if (visiblePayload.length === 0) {
    return null;
  }

  const formattedLabel =
    label !== undefined && labelFormatter
      ? labelFormatter(label, visiblePayload[0]?.payload)
      : label;

  return (
    <div className="rounded-md bg-white/95 px-3 py-2 shadow-lg shadow-slate-200/80 backdrop-blur-sm">
      {formattedLabel !== undefined && (
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          {formattedLabel}
        </div>
      )}
      <div className="space-y-1">
        {visiblePayload.map((item: ChartTooltipPayload, index: number) => {
          const [value, name] =
            valueFormatter && item.value !== undefined
              ? valueFormatter(item.value, item.name ?? '')
              : [String(item.value ?? ''), item.name ?? ''];
          const dataPointColor = item.payload?.[CHART_DATA_POINT_COLOR_KEY];
          const markerColor =
            typeof dataPointColor === 'string'
              ? dataPointColor
              : item.color ?? item.fill ?? fallbackColors[index % fallbackColors.length];

          return (
            <div key={`${item.dataKey}-${index}`} className="flex min-w-[140px] items-center gap-2">
              <span
                className={`h-2.5 w-2.5 ${markerClassName}`}
                style={{
                  backgroundColor: markerColor,
                }}
              />
              <span className="flex-1 text-xs text-gray-600">{name}</span>
              <span className="text-sm font-semibold text-gray-900">{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ChartLegendContent = ({
  payload = [],
  markerClassName = 'rounded-full',
  aside = false,
  onToggleSeries,
}: ChartLegendContentProps): JSX.Element | null => {
  if (payload.length === 0) {
    return null;
  }

  return (
    <div className={aside ? 'h-full w-full px-1' : 'w-full px-1 pt-2'}>
      <div className={aside ? 'flex flex-col gap-1.5' : 'flex flex-wrap gap-x-3 gap-y-1'}>
        {payload.map((item: ChartLegendPayload) => {
          const entryContent = (
            <>
              <span
                className={`mt-1 h-2 w-2 shrink-0 ${markerClassName}`}
                style={{ backgroundColor: item.inactive ? '#cbd5e1' : item.color ?? '#64748b' }}
              />
              <span
                className={`min-w-0 whitespace-normal break-words text-[11px] leading-4 ${
                  item.inactive ? 'text-slate-400 line-through' : 'text-slate-600'
                }`}
              >
                {item.value}
              </span>
            </>
          );

          if (onToggleSeries && item.id) {
            const dataKey = item.id;

            return (
              <button
                key={dataKey}
                type="button"
                className="flex min-w-0 max-w-full cursor-pointer items-start gap-1.5 border-0 bg-transparent p-0 text-left hover:opacity-70"
                onClick={(): void => onToggleSeries(dataKey)}
              >
                {entryContent}
              </button>
            );
          }

          return (
            <div
              key={item.id ?? item.value}
              className="flex min-w-0 max-w-full items-start gap-1.5"
            >
              {entryContent}
            </div>
          );
        })}
      </div>
    </div>
  );
};
